import popupStyles from "./singleUserTokenModal.module.css";
import { IPopupWithoutProps } from "../../../types/types";
import venchyImg from "../../../images/questionnaire_doctor.png";
import { tWithKeyAttr as t } from "../../../i18nHelper";
import { useState } from "react";
import { PaymentOperation } from "../../../services/types/utils.types";

export type TokenDeliveryMethod = "sendToUser" | "displayHere";

export interface SingleUserTokenConfirmData {
  deliveryMethod: TokenDeliveryMethod;
  invoiceUserName?: string;
  maxNumOfPayments?: number;
  customMessage?: string;
}

interface SingleUserTokenModalProps extends IPopupWithoutProps {
  onConfirm: (data: SingleUserTokenConfirmData) => void;
  userName: string;
  isProcessing?: boolean;
  operation?: PaymentOperation;
}

function SingleUserTokenModal({
  onClose,
  onConfirm,
  userName,
  isProcessing = false,
  operation,
}: SingleUserTokenModalProps) {
  const [deliveryMethod, setDeliveryMethod] = useState<TokenDeliveryMethod>("sendToUser");
  const [invoiceUserName, setInvoiceUserName] = useState<string>(userName);
  const [maxNumOfPayments, setMaxNumOfPayments] = useState<number>(4);
  const [customMessage, setCustomMessage] = useState<string>("");

  const showChargeFields = operation === 'ChargeOnly' || operation === 'ChargeAndToken';

  return (
    <article className={popupStyles.popup} data-testid="single-user-token-popup">
      <img className={popupStyles.popup__img} src={venchyImg} alt={t('VENCHI')} />
      <h2 className={popupStyles.popup__title}>{t('PAYMENT_TOKEN_CREATION_LINK')}</h2>
      <p className={popupStyles.popup__subtitle}>
        {t('PAYMENT_TOKEN_CREATION_FOR_USER', { userName })}
      </p>
      
      <div className={popupStyles.popup__options}>
        <label className={popupStyles.popup__option}>
          <input
            type="radio"
            name="deliveryMethod"
            value="sendToUser"
            checked={deliveryMethod === "sendToUser"}
            onChange={() => setDeliveryMethod("sendToUser")}
            disabled={isProcessing}
          />
          <div className={popupStyles.popup__optionContent}>
            <span className={popupStyles.popup__optionTitle}>
              {t('SEND_LINK_TO_USER')}
            </span>
            <span className={popupStyles.popup__optionDesc}>
              {t('SEND_LINK_TO_USER_DESC')}
            </span>
          </div>
        </label>

        <label className={popupStyles.popup__option}>
          <input
            type="radio"
            name="deliveryMethod"
            value="displayHere"
            checked={deliveryMethod === "displayHere"}
            onChange={() => setDeliveryMethod("displayHere")}
            disabled={isProcessing}
          />
          <div className={popupStyles.popup__optionContent}>
            <span className={popupStyles.popup__optionTitle}>
              {t('DISPLAY_LINK_HERE')}
            </span>
            <span className={popupStyles.popup__optionDesc}>
              {t('DISPLAY_LINK_HERE_DESC')}
            </span>
          </div>
        </label>
      </div>

      <div className={popupStyles.popup__fields}>
        <div className={popupStyles.popup__field}>
          <label className={popupStyles.popup__fieldLabel}>
            {t('CUSTOM_MESSAGE_LABEL')}
          </label>
          <textarea
            className={popupStyles.popup__fieldInput}
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            disabled={isProcessing}
            maxLength={500}
            placeholder={t('CUSTOM_MESSAGE_PLACEHOLDER')}
            rows={3}
            style={{ resize: 'vertical', minHeight: '60px' }}
            data-testid="custom-message-input"
          />
        </div>
      </div>

      {showChargeFields && (
        <div className={popupStyles.popup__fields}>
          <div className={popupStyles.popup__field}>
            <label className={popupStyles.popup__fieldLabel}>
              {t('INVOICE_USER_NAME_LABEL')}
            </label>
            <input
              type="text"
              className={popupStyles.popup__fieldInput}
              value={invoiceUserName}
              onChange={(e) => setInvoiceUserName(e.target.value)}
              disabled={isProcessing}
              data-testid="invoice-user-name-input"
            />
          </div>
          <div className={popupStyles.popup__field}>
            <label className={popupStyles.popup__fieldLabel}>
              {t('MAX_NUM_OF_PAYMENTS_LABEL')}
            </label>
            <select
              size={6}
              className={`${popupStyles.popup__fieldInput} ${popupStyles.popup__fieldSelect}`}
              value={maxNumOfPayments}
              onChange={(e) => setMaxNumOfPayments(Number(e.target.value))}
              disabled={isProcessing}
              data-testid="max-num-of-payments-input"
            >
              {Array.from({ length: 36 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className={popupStyles.popup__buttons}>
        <button
          className={`button ${popupStyles.popup__confirmBtn} hover`}
          data-testid="confirm-single-user-token-button"
          onClick={() => onConfirm({
            deliveryMethod,
            ...(showChargeFields ? { invoiceUserName, maxNumOfPayments } : {}),
            ...(customMessage ? { customMessage } : {}),
          })}
          disabled={isProcessing}
        >
          {isProcessing ? t('PROCESSING') : t('CONFIRM')}
        </button>
        <button
          className={`button ${popupStyles.popup__cancelBtn} hover`}
          data-testid="cancel-single-user-token-button"
          onClick={onClose}
          disabled={isProcessing}
        >
          {t('CANCEL')}
        </button>
      </div>
    </article>
  );
}

export default SingleUserTokenModal;
